import { useEffect, useState } from "react";

/**
 * The gate in front of licensed music.
 *
 * The score data has always carried `publicSafe: false` on the five songs NSM
 * licenses rather than owns — Over the Rainbow (both keys), Bare Necessities,
 * Meet the Flintstones, Puff the Magic Dragon — with the note "never render
 * these outside the paywall". Nothing ever read the flag. The intention sat in
 * the data while the engraving rendered to anyone who guessed the URL.
 *
 * What counts as proof of access: the lesson is being shown INSIDE the school,
 * i.e. framed by the LMS. That is not a token, but it is not decorative
 * either — the app sends `Content-Security-Policy: frame-ancestors` naming
 * learn-music.nathanielschool.com, so no other site can frame this page, and
 * the LMS only hands the lesson URL to a signed-in student who owns the
 * course. A direct visit is not framed, and is exactly the hole being closed.
 *
 * A signed access token passed from the LMS would be stronger, and is the
 * right next step; it needs a shared secret in both deployments, which this
 * change deliberately does not touch.
 */

export function useInsideSchool(): boolean | null {
  // null while unknown, so the page never flashes the wrong state.
  const [inside, setInside] = useState<boolean | null>(null);
  useEffect(() => {
    try {
      setInside(window.self !== window.top);
    } catch {
      // A cross-origin parent throws on access — which itself means framed.
      setInside(true);
    }
  }, []);
  return inside;
}

export function PaidContentGate({ title, composer }: { title: string; composer?: string }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-nsm-dim">
        Part of the course
      </p>
      <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-nsm-ink">
        {title}
      </h1>
      {composer && <p className="mt-1 text-[13px] text-nsm-dim">{composer}</p>}
      <p className="mt-4 text-[15px] leading-relaxed text-nsm-dim">
        This piece is a licensed song, so we can only show it inside the school
        to students who own the course. Sign in and open it from your Sight
        Reading lessons and it will play, transpose and score exactly as the
        rest of the course does.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href="https://learn-music.nathanielschool.com"
          className="rounded-lg bg-nsm-ink px-4 py-2 text-[13px] font-semibold text-nsm-bg transition hover:opacity-90"
        >
          Sign in to the school
        </a>
        <a
          href="/course/sight-reading"
          className="rounded-lg border border-nsm-border px-4 py-2 text-[13px] font-semibold text-nsm-ink transition hover:bg-nsm-surface"
        >
          See the whole course
        </a>
      </div>
      <p className="mt-8 text-[13px] text-nsm-dim">
        Twenty-five of the thirty pieces are Jason&rsquo;s own or out of
        copyright, and those are open to read right now.
      </p>
    </div>
  );
}
